import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
} from 'react'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import { rebuild } from 'react-tooltip'

import { PageDescription } from '../../../../components'
import Table, { Col, TableFooter } from '../../../../components/Table/next'
import ReportActions from '../../../../components/ReportActions'
import ReportMenu from '../../Menu'
import OverviewReportFilterDisplay from '../Filters'

import { prepareDatesForApi } from '../../../../utils/dates'

import {
  OverviewReportGroupBy,
  OverviewReportTableContext,
  OverviewReportVariables,
} from './index'
import { State } from '../../../../reducers'
import { UserStore } from '../../../../typings/stores/UserStore'

import styles from './styles.module.css'
import accounting from 'accounting'
import { dumpData } from '../../../../actions/reports'
interface OverviewReportTableProps {
  groupBy: OverviewReportGroupBy
  cols: Col[]
  footerPadding: number
  data: any
  loading: boolean
  error: boolean
  getData: ({ variables: OverviewReportVariables }) => void
  exportData: any
  exportLoading: boolean
  exportError: boolean
  getExportData: ({ variables }) => void
}

const OverviewReportTable: FunctionComponent<OverviewReportTableProps> = ({
  groupBy,
  cols,
  footerPadding,
  getData,
  data,
  loading,
  error,
  getExportData,
  exportData,
  exportLoading,
  exportError,
}) => {
  const user = useSelector<State, UserStore>(state => state.user)
  const {
    changePage,
    changePageSize,
    filters,
    pagination: { size, page },
    setFilter,
    setFilters,
    sort,
    changeSort,
  } = useContext(OverviewReportTableContext)
  useEffect(() => {
    rebuild()
  })

  const fetchExport = () => {
    getExportData({
      variables: {
        ...updateVariables(),
        take: get(data, `overviewReporting.pagination.totalEntries`, 0),
      },
    })
  }

  const updateVariables = () => {
    const { start, end } = filters
    return {
      ...prepareDatesForApi(start, end),
      skip: (page - 1) * size,
      take: size,
      sorts: sort.by
        ? [
            {
              [sort.by]: sort.dir.toUpperCase(),
            },
          ]
        : [
            {
              verticalName: 'ASC',
            },
          ],
      externalPropertyId: user.activeWebsite.id.toString(),
    } as OverviewReportVariables
  }
  const fetchData = useCallback(() => {
    getData({ variables: updateVariables() })
  }, [filters, size, page, sort])

  useEffect(() => {
    dumpData('overviewReport', exportData?.overviewReporting?.page, {
      ...filters,
    })
  }, [exportData])

  useEffect(fetchData, [sort, page, size])

  const getTotal = (data, param) =>
    data.reduce((sum, page) => sum + page[param], 0)

  const pagination = {
    currentPage: get(data, `overviewReporting.pagination.currentPage`, 1),
    pageSize: get(data, `overviewReporting.pagination.pageSize`, 10),
    total: get(data, `overviewReporting.pagination.totalEntries`, 0),
  }

  const totalRowBuilder: TableFooter[] = [
    {
      text: 'Totals',
      colSpan: footerPadding - 4,
      className: styles.footerHeader,
    },
    {
      text: data
        ? accounting.formatNumber(
            getTotal(data.overviewReporting.page, 'totalImpressions')
          )
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? accounting.formatNumber(
            getTotal(data.overviewReporting.page, 'viewableImpressions')
          )
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? accounting.formatNumber(
            getTotal(data.overviewReporting.page, 'productClicks')
          )
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? accounting.formatNumber(
            getTotal(data.overviewReporting.page, 'uniqueClicks')
          )
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? (getTotal(data.overviewReporting.page, 'totalCTR') * 100).toFixed(2) +
          '%'
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? (getTotal(data.overviewReporting.page, 'viewableCTR') * 100).toFixed(
            2
          ) + '%'
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? accounting.formatNumber(
            getTotal(data.overviewReporting.page, 'sales')
          )
        : 0,
      className: styles.right,
    },
    {
      text: data
        ? (
            getTotal(data.overviewReporting.page, 'conversionRate') * 100
          ).toFixed(2) + '%'
        : 0,
      className: styles.right,
    },
    {
      text:
        data &&
        accounting.formatMoney(
          getTotal(data.overviewReporting.page, 'commission'),
          '$'
        ),
      className: styles.right,
    },
    {
      text:
        data &&
        accounting.formatMoney(
          getTotal(data.overviewReporting.page, 'epc'),
          '$'
        ),
      className: styles.right,
    },
  ]

  return (
    <>
      <ReportMenu />

      <PageDescription content="This is an aggregated report that provides sales funnel data based on click date." />

      <OverviewReportFilterDisplay
        filters={filters}
        setFilter={setFilter}
        setFilters={setFilters}
      />

      <ReportActions
        fetchReport={fetchData}
        fetchExport={fetchExport}
        disabled={!get(data, `overviewReporting.pagination.totalEntries`, 0)}
        reportStatus={{ loading: loading, hasError: error }}
        exportStatus={{ loading: exportLoading, hasError: exportError }}
      />

      <Table
        className={styles.table}
        data={data && get(data, `overviewReporting.page`)}
        cols={cols}
        totalRow={totalRowBuilder}
        sort={sort}
        onSortChange={changeSort}
        pagination={{
          onPageSizeUpdate: changePageSize,
          onPaginate: changePage,
          ...pagination,
        }}
        isLoading={loading}
      />
    </>
  )
}

export default OverviewReportTable
